<template>
  <div class="min-h-screen bg-gray-100">
    <div class="mx-auto p-6">
      <div
        class="flex justify-between items-end mb-6 mdmax:flex-col mdmax:items-stretch gap-4"
      >
        <div class="flex-1 mdmax:w-full">
          <!-- Row 1: Customer & Search -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="text-base text-neutral-700 font-medium mb-1 inline-block">
                Customer
              </label>
              <CustomSelect
                :options="customers"
                :default="selectedCustomer"
                optionLabel="customer_name"
                placeholder="Pilih customer"
                @input="(val) => { selectedCustomer = val }"
              />
            </div>
            <div>
              <label class="text-base text-neutral-700 font-medium mb-1 inline-block">
                Search
              </label>
              <TextField
                type="text"
                v-model="search"
                placeholder="Cari invoice..."
                @enter="onFilterChange"
              />
            </div>
          </div>

          <!-- Row 2: Dates & Buttons -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
            <div>
              <label class="text-base text-neutral-700 font-medium mb-1 inline-block">
                Start Date
              </label>
              <TextField type="date" v-model="startDate" />
            </div>
            <div>
              <label class="text-base text-neutral-700 font-medium mb-1 inline-block">
                End Date
              </label>
              <TextField type="date" v-model="endDate" />
            </div>
            <div class="flex gap-2 w-full mt-2 md:mt-0">
              <button
                @click="onFilterChange"
                class="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium"
              >
                Apply
              </button>
              <button
                @click="resetFilter"
                class="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 font-medium"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex mdmax:w-full justify-end mb-4">
        <button
          @click="openCreateInvoiceModal"
          class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium"
        >
          + Buat Invoice
        </button>
      </div>

      <TableComponent :columns="columns" :list="tableData">
        <template #table-row="{ entry, column }">
          <span v-if="column.field === 'action'" class="flex gap-2">
            <button
              @click="$router.push(`/invoices/invoices/${entry.id || entry.invoice_id}`)"
              class="px-3 py-1 rounded bg-neutral-200 text-neutral-700 hover:bg-neutral-300 text-sm"
            >
              Detail
            </button>
            <button
              @click="openDeleteInvoiceModal(entry)"
              class="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm"
            >
              Delete
            </button>
          </span>

          <span v-else-if="column.field === 'RowNumber'" class="font-medium">
            {{ entry.RowNumber }}
          </span>

          <span v-else-if="column.field === 'invoice_date'">
            {{ formatDate(entry.invoice_date) }}
          </span>

          <span v-else-if="column.field === 'total_amount'">
            {{ formatCurrency(entry.total_amount || entry.total_price || entry.total) }}
          </span>

          <span v-else>{{ entry[column.field] || "-" }}</span>
        </template>
      </TableComponent>

      <div
        v-if="tableData.length > 0"
        class="mt-6 mb-6 flex justify-between items-center mdmax:flex-col mdmax:items-start gap-4"
      >
        <div v-if="!isMobile" class="text-sm font-medium text-neutral-500">
          Menampilkan {{ start }} - {{ end }} dari {{ totalRecords }}
        </div>
        <Pagination
          :currentPage="page"
          :totalCount="totalRecords"
          :pageSize="limit"
          @onPageChange="changePage"
        />
      </div>

      <Modal
        :modalVisible="createModalVisible"
        @close="closeCreateInvoiceModal"
        width="900px"
      >
        <div slot="modal-content" class="text-left">
          <h3 class="text-lg font-bold text-neutral-800 mb-2">
            Buat Invoice
          </h3>
          <p class="text-sm text-neutral-500 mb-6">
            Step {{ createStep }} dari 3
          </p>

          <div v-if="createStep === 1">
            <div class="mb-4">
              <TextField
                label="Invoice Date"
                type="date"
                required
                v-model="createForm.invoice_date"
              />
            </div>

            <div>
              <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">
                Customer
              </label>
              <CustomSelect
                :options="customers"
                :default="createFormCustomer"
                optionLabel="customer_name"
                placeholder="Pilih customer"
                @input="onCustomerSelect"
              />
            </div>
            
            <div class="mt-4">
              <Textarea
                label="Pickup Address"
                required
                :disabled="!createFormCustomer"
                v-model="createForm.pickup_address"
                placeholder="Alamat asal/pickup"
                heightClass="h-24"
              />
            </div>
          </div>

          <div v-else-if="createStep === 2">
            <div class="flex justify-between items-end mb-4 mdmax:flex-col gap-3">
              <div>
                <p class="text-sm text-neutral-500">Customer</p>
                <p class="font-bold text-neutral-800">
                  {{ createFormCustomer?.customer_name || "-" }}
                </p>
              </div>
              <div class="flex gap-2">
                <button
                  @click="loadEligibleResi"
                  class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50 font-medium"
                >
                  Reload Eligible Resi
                </button>
              </div>
            </div>

            <div class="mb-4">
              <TextField
                label="Search resi"
                type="text"
                v-model="resiSearch"
                placeholder="Cari resi_number / destination"
              />
            </div>

            <div class="bg-white rounded-lg p-3 mb-4 shadow-sm">
              <label class="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  class="w-4 h-4"
                  :checked="isAllResiSelected"
                  @change="toggleSelectAllResi($event.target.checked)"
                />
                Select all (filtered)
              </label>
            </div>

            <TableComponent :columns="resiColumns" :list="filteredEligibleResi" compact>
              <template #table-row="{ entry, column }">
                <span v-if="column.field === 'selected'">
                  <input
                    type="checkbox"
                    class="w-4 h-4"
                    :checked="selectedResiIdsSet.has(entry.id || entry.resi_id)"
                    @change="toggleResi(entry, $event.target.checked)"
                  />
                </span>
                <span v-else-if="column.field === 'date'">
                  {{ formatDate(entry.date) }}
                </span>
                <span v-else-if="column.field === 'total_price'">
                  {{ formatCurrency(entry.total_price) }}
                </span>
                <span v-else>{{ entry[column.field] || "-" }}</span>
              </template>
            </TableComponent>
          </div>

          <div v-else>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label="VAT Percent"
                type="number"
                v-model="createForm.vat_percent"
                placeholder="1.1"
              />
              <TextField
                label="Discount Percent"
                type="number"
                v-model="createForm.discount_percentage"
                placeholder="0"
              />
              <TextField
                label="Invoice Number (Opsional)"
                type="text"
                v-model="createForm.invoice_number"
                placeholder="INV-001"
              />
            </div>

            <div class="mt-4 bg-white rounded-lg p-4 shadow-sm">
              <p class="text-sm text-neutral-500 mb-1">Resi dipilih</p>
              <p class="text-neutral-800 font-bold">
                {{ selectedResiIds.length }} resi
              </p>
            </div>
          </div>

          <div class="flex justify-between gap-3 mt-8">
            <button
              v-if="createStep > 1"
              @click="createStep--"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Back
            </button>
            <div class="flex-1"></div>
            <button
              @click="onNextCreateStep"
              class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="nextDisabled"
            >
              {{ createStep === 3 ? "Submit" : "Next" }}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        :modalVisible="deleteModalVisible"
        @close="closeDeleteInvoiceModal"
        width="400px"
      >
        <div slot="modal-content" class="text-center">
          <h3 class="text-lg font-bold text-neutral-800 mb-2">
            Hapus Invoice
          </h3>
          <p class="text-neutral-600 mb-6">
            Apakah Anda yakin ingin menghapus invoice
            <strong>{{ invoiceToDelete?.invoice_number }}</strong>?
          </p>
          <div class="flex justify-center gap-3">
            <button
              @click="closeDeleteInvoiceModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="confirmDeleteInvoice"
              class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
            >
              Hapus
            </button>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import dayjs from "dayjs"
import { mapActions, mapGetters } from "vuex"
import TableComponent from "@/components/Table/Table"
import Pagination from "@/components/Pagination/Pagination"
import TextField from "@/components/Forms/TextField"
import Textarea from "@/components/Forms/Textarea"
import CustomSelect from "@/components/Forms/CustomSelect"
import Modal from "@/components/Modal/Modal"

export default {
  name: "InvoicesInvoices",
  components: {
    TableComponent,
    Pagination,
    TextField,
    Textarea,
    CustomSelect,
    Modal
  },
  data() {
    return {
      customers: [],
      selectedCustomer: null,
      startDate: "",
      endDate: "",
      search: "",

      page: 1,
      limit: 10,
      meta: {},
      list: [],
      columns: [
        { label: "No", field: "RowNumber" },
        { label: "Invoice Number", field: "invoice_number" },
        { label: "Invoice Date", field: "invoice_date" },
        { label: "Customer", field: "customer_name" },
        { label: "Total", field: "total_amount" },
        { label: "Action", field: "action" }
      ],

      createModalVisible: false,
      createStep: 1,
      createFormCustomer: null,
      eligibleResi: [],
      resiSearch: "",
      selectedResiIds: [],
      createForm: {
        invoice_date: dayjs().format("YYYY-MM-DD"),
        vat_percent: "1.1",
        invoice_number: "",
        pickup_address: "",
        discount_percentage: "0"
      },
      resiColumns: [
        { label: "", field: "selected" },
        { label: "Resi Number", field: "resi_number" },
        { label: "Date", field: "date" },
        { label: "Destination", field: "destination" },
        { label: "Total Price", field: "total_price" }
      ],

      deleteModalVisible: false,
      invoiceToDelete: null
    }
  },
  computed: {
    ...mapGetters("common", ["isMobile"]),
    totalRecords() {
      return (
        this.meta?.total_item ||
        this.meta?.total ||
        this.meta?.totalItems ||
        0
      )
    },
    tableData() {
      return (this.list || []).map((item, index) => ({
        ...item,
        RowNumber: (this.page - 1) * this.limit + index + 1
      }))
    },
    start() {
      return this.totalRecords ? (this.page - 1) * this.limit + 1 : 0
    },
    end() {
      return Math.min(this.page * this.limit, this.totalRecords)
    },

    selectedResiIdsSet() {
      return new Set(this.selectedResiIds)
    },
    filteredEligibleResi() {
      const term = (this.resiSearch || "").toLowerCase()
      if (!term) return this.eligibleResi
      return (this.eligibleResi || []).filter((r) => {
        const resiNumber = String(r.resi_number || "").toLowerCase()
        const destination = String(r.destination || "").toLowerCase()
        return resiNumber.includes(term) || destination.includes(term)
      })
    },
    isAllResiSelected() {
      const filtered = this.filteredEligibleResi || []
      if (filtered.length === 0) return false
      return filtered.every((r) =>
        this.selectedResiIdsSet.has(r.id || r.resi_id)
      )
    },
    nextDisabled() {
      if (this.createStep === 1) return !this.createFormCustomer || !this.createForm.invoice_date || !this.createForm.pickup_address
      if (this.createStep === 2) return this.selectedResiIds.length === 0
      return !this.createForm.vat_percent || this.createForm.discount_percentage === ""
    }
  },
  methods: {
    ...mapActions("common", ["showLoading", "hideLoading", "setToast"]),
    toastSuccess(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "success",
          duration: 2500,
          primaryText: message,
          secondaryText
        }
      })
    },
    toastError(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "danger",
          duration: 3500,
          primaryText: message,
          secondaryText
        }
      })
    },
    getErrorMessage(err, fallback = "Terjadi kesalahan") {
      return (
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        fallback
      )
    },
    unwrapList(envelope) {
      const data = envelope?.data?.data
      const meta = envelope?.data?.meta || envelope?.data?.data?.meta || {}
      const items = Array.isArray(data) ? data : data?.items || data?.data || []
      return { items, meta }
    },
    toDecimalString(value, fallback = "0") {
      const raw = String(value ?? "").trim()
      if (!raw) return fallback
      const normalized = raw.replace(/,/g, ".").replace(/[^0-9.-]/g, "")
      return normalized || fallback
    },
    formatCurrency(value) {
      const numberValue = Number(this.toDecimalString(value, "0"))
      if (Number.isNaN(numberValue)) return "Rp 0"
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0
      }).format(numberValue)
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric"
      }).format(date);
    },

    async fetchCustomers() {
      try {
        const res = await api.invoiceGenerator.customers({ page: 1, per_page: 500 })
        const { items } = this.unwrapList(res)
        this.customers = items
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat customer"))
      }
    },
    async fetchInvoices() {
      this.showLoading()
      try {
        const customerId =
          this.selectedCustomer?.id || this.selectedCustomer?.customer_id
        const res = await api.invoiceGenerator.invoices({
          page: this.page,
          per_page: this.limit,
          customer_id: customerId || undefined,
          start_date: this.startDate || undefined,
          end_date: this.endDate || undefined,
          search: this.search || undefined
        })
        const { items, meta } = this.unwrapList(res)
        this.list = items
        this.meta = meta || {}
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat invoices"))
      } finally {
        this.hideLoading()
      }
    },
    onFilterChange() {
      this.page = 1
      this.fetchInvoices()
    },
    resetFilter() {
      this.selectedCustomer = null
      this.startDate = ""
      this.endDate = ""
      this.search = ""
      this.onFilterChange()
    },
    changePage(val) {
      this.page = val
      this.fetchInvoices()
    },

    openCreateInvoiceModal() {
      this.createStep = 1
      this.createFormCustomer = this.selectedCustomer
      this.eligibleResi = []
      this.resiSearch = ""
      this.selectedResiIds = []
      this.createForm = {
        invoice_date: dayjs().format("YYYY-MM-DD"),
        vat_percent: "1.1",
        invoice_number: "",
        pickup_address: "",
        discount_percentage: "0"
      }
      this.createModalVisible = true
    },
    closeCreateInvoiceModal() {
      this.createModalVisible = false
    },
    onCustomerSelect(val) {
      this.createFormCustomer = val
      if (val) {
        this.createForm.pickup_address = val.customer_address || ""
      } else {
        this.createForm.pickup_address = ""
      }
    },
    async loadEligibleResi() {
      const customerId =
        this.createFormCustomer?.id || this.createFormCustomer?.customer_id
      if (!customerId) return

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.resi({
          page: 1,
          per_page: 500,
          customer_id: customerId,
          eligible_only: true
        })
        const { items } = this.unwrapList(res)
        this.eligibleResi = items
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat eligible resi"))
      } finally {
        this.hideLoading()
      }
    },
    toggleResi(entry, checked) {
      const id = entry.id || entry.resi_id
      if (!id) return
      const exists = this.selectedResiIds.includes(id)
      if (checked && !exists) this.selectedResiIds.push(id)
      if (!checked && exists)
        this.selectedResiIds = this.selectedResiIds.filter((x) => x !== id)
    },
    toggleSelectAllResi(checked) {
      const filtered = this.filteredEligibleResi || []
      const ids = filtered.map((r) => r.id || r.resi_id).filter(Boolean)
      const set = new Set(this.selectedResiIds)
      if (checked) {
        ids.forEach((id) => set.add(id))
        this.selectedResiIds = Array.from(set)
        return
      }
      ids.forEach((id) => set.delete(id))
      this.selectedResiIds = Array.from(set)
    },

    async onNextCreateStep() {
      if (this.createStep === 1) {
        await this.loadEligibleResi()
        this.createStep = 2
        return
      }
      if (this.createStep === 2) {
        this.createStep = 3
        this.createForm.discount_percentage = this.createFormCustomer?.discount_percent || "0"
        return
      }

      const customerId =
        this.createFormCustomer?.id || this.createFormCustomer?.customer_id
      if (!customerId) return

      const payloads = {
        invoice_customer_id: customerId,
        resi_ids: this.selectedResiIds,
        invoice_date: this.createForm.invoice_date,
        vat_percent: this.toDecimalString(this.createForm.vat_percent, "1.1"),
        pickup_address: this.createForm.pickup_address,
        discount_percentage: this.toDecimalString(this.createForm.discount_percentage, "0")
      }
      if (this.createForm.invoice_number) {
        payloads.invoice_number = this.createForm.invoice_number
      }

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.createInvoice(payloads)
        this.toastSuccess(res?.data?.message || "Invoice berhasil dibuat")
        const createdId = res?.data?.data?.id || res?.data?.data?.invoice_id
        this.closeCreateInvoiceModal()
        await this.fetchInvoices()
        if (createdId) {
          this.$router.push(`/invoices/invoices/${createdId}`)
        }
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal membuat invoice"))
      } finally {
        this.hideLoading()
      }
    },
    
    openDeleteInvoiceModal(entry) {
      this.invoiceToDelete = entry
      this.deleteModalVisible = true
    },
    closeDeleteInvoiceModal() {
      this.deleteModalVisible = false
      this.invoiceToDelete = null
    },
    async confirmDeleteInvoice() {
      if (!this.invoiceToDelete) return
      const invoiceId = this.invoiceToDelete.id || this.invoiceToDelete.invoice_id

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.deleteInvoice(invoiceId)
        this.toastSuccess(res?.data?.message || "Invoice berhasil dihapus")
        this.closeDeleteInvoiceModal()
        await this.fetchInvoices()
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal menghapus invoice"))
      } finally {
        this.hideLoading()
      }
    }
  },
  async mounted() {
    await this.fetchCustomers()
    this.fetchInvoices()
  }
}
</script>

